/**
 * The Sparrow artwork, ported verbatim from docs/mockups/sparrow.html (the approved
 * brand: logo direction B "Dart", the character Nib v3). Framework-free SVG strings, so
 * the web app (DartMark, Nib), the icon/og renders and the OG worker's resvg cards all
 * draw from the same path data. Don't redraw these: change the mock, then port again.
 */

export const BRAND_COLOURS = {
	ink: '#171714',
	paper: '#f4f0e6',
	stock: '#fcf9f2',
	ver: '#ff4f32',
} as const;
const C = BRAND_COLOURS;

// ---------------------------------------------------------------------------- the Dart

/**
 * B · Dart: a sparrow seen from above in a stoop. Drawn pointing right in a 100 box,
 * rotated 45° to dive ↘. Head + beak at the tip, the wings are the arrowhead's barbs, a
 * notched tail is the fletching.
 */
export const DART_D =
	'M68,37 C58,25 46,13 28,3 C34,16 40,26 46,31 L52,41 L28,42 L12,31 L19,50 L12,69 L28,58 L52,59 L46,69 C40,74 34,84 28,97 C46,87 58,75 68,63 Z';
const DART_HEAD = '<circle cx="71" cy="50" r="15.5"/><path d="M81,38.5 L99,50 L81,61.5Z"/>';

/** The Dart at scale `s`, its unrotated box's origin at (x, y). */
export function dartPath(fill: string, s = 1, x = 0, y = 0): string {
	return `<g transform="translate(${x} ${y}) scale(${s})"><g transform="rotate(45 55.5 50) translate(-5.5 0)" fill="${fill}"><path d="${DART_D}"/>${DART_HEAD}</g></g>`;
}

/**
 * The Dart's optical centre at scale 1 (measured in the mock): halfway between its
 * bounding-box centre and its centre of mass, so the heavy head gets a little more room.
 */
export const DART_OPT = [45, 39.5] as const;

/** The Dart at scale `k` with its optical centre on (cx, cy): in a 100 box by default. */
export function dartCentred(fill: string, k = 0.8, cx = 50, cy = 50): string {
	return dartPath(fill, k, round(cx - k * DART_OPT[0]), round(cy - k * DART_OPT[1]));
}

/** The brand mark beside the wordmark (the mock's `dartMark`): a 100 box, nearly filled. */
export const DART_MARK_K = 0.98;

/** The app icon (the mock's `dartIcon`): a vermilion tile, the ink Dart optically centred. */
export function dartIcon({ bg = C.ver, fg = C.ink, r = 22.5, k = 0.8 }: { bg?: string; fg?: string; r?: number; k?: number } = {}): string {
	return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" rx="${r}" fill="${bg}"/>${dartCentred(fg, k)}</svg>`;
}

// ------------------------------------------------------------------------------- Nib v3

type Eye = 'dot' | 'happy' | 'smug' | 'worry' | 'shut';

function nibEye(eye: Eye, ex: number, ey: number, ink: string, face: string): string {
	if (eye === 'dot') return `<circle cx="${ex}" cy="${ey}" r="4.1" fill="${ink}"/><circle cx="${round(ex + 1.4)}" cy="${round(ey - 1.4)}" r="1.25" fill="${face}"/>`;
	if (eye === 'happy') return `<path d="M${round(ex - 4.6)},${round(ey + 1.8)} Q${ex},${round(ey - 4.6)} ${round(ex + 4.6)},${round(ey + 1.8)}" fill="none" stroke="${ink}" stroke-width="3" stroke-linecap="round"/>`;
	if (eye === 'smug')
		return `<path d="M${round(ex - 4.3)},${round(ey - 0.4)} a4.3,4.3 0 0 0 8.6,0Z" fill="${ink}"/><path d="M${round(ex - 5.8)},${round(ey - 0.4)} L${round(ex + 5.6)},${round(ey - 1.6)}" stroke="${ink}" stroke-width="2.6" stroke-linecap="round"/>`;
	if (eye === 'worry') return `<circle cx="${ex}" cy="${round(ey + 0.6)}" r="3.3" fill="${ink}"/><circle cx="${round(ex + 1)}" cy="${round(ey - 0.5)}" r="1" fill="${face}"/>`;
	return `<path d="M${round(ex - 4.4)},${ey} Q${ex},${round(ey + 3.6)} ${round(ex + 4.4)},${ey}" fill="none" stroke="${ink}" stroke-width="2.8" stroke-linecap="round"/>`;
}

export interface NibOptions {
	ink?: string;
	face?: string;
	tail?: string;
	mono?: boolean;
	eye?: Eye;
	beak?: 'shut' | 'open';
	wing?: 'fold' | 'lift' | 'droop';
	/** Head turn, degrees. */
	head?: number;
	/** Whole-body lean, degrees. */
	tilt?: number;
	ruffle?: boolean;
	legs?: boolean;
	/** Eye offset. */
	ex?: number;
	ey?: number;
	/** Leg colour (default ink): the inverted Nib on an ink card has stock legs. */
	legsColour?: string;
	/** A seasonal hat, keyed off the puzzle type: a witch's hat (Halloween) or a Santa hat (Christmas). */
	hat?: 'witch' | 'santa';
}

/** Nib v3 (the mock's `nib()`): built in a 120×120 box, facing left, standing on y=106. */
export function nib(o: NibOptions = {}): string {
	const ink = o.ink ?? C.ink;
	const face = o.face ?? C.stock;
	const tailC = o.mono ? ink : (o.tail ?? C.ver);
	const legC = o.legsColour ?? ink;
	const eye = o.eye ?? 'dot';
	const beak = o.beak ?? 'shut';
	const wing = o.wing ?? 'fold';
	const headR = o.head ?? 0;
	const tilt = o.tilt ?? 0;
	const legs = o.legs !== false;
	const BODY = 'M48,22 C60,28 79,43 90,60 L86,74 C78,84 64,90 52,90 C38,90 29.5,80 29,66 C28.5,55 30.5,47 34,42Z';
	const TAIL = 'M83,53 L104,89 L74,79Z';
	const BEAK = beak === 'open' ? 'M26,31.5 L11,36 L26,38.5Z M26,40.5 L14,43.5 L26,45Z' : 'M26,33 L10.5,39.5 L26,44.5Z';
	const LEAF = 'M25,32 C33,26 46,27.5 53,37 C45,43.5 34,46 25,43Z';
	const WINGS = {
		fold: 'M50,50 C62,44 79,51 89,66 C75,71 59,68 51,59Z',
		lift: 'M52,56 C54,41 69,29 91,26 C85,35 88,42 81,50 C83,54 77,59 72,61 C64,64 56,63 52,56Z',
		droop: 'M50,54 C61,50 76,60 82,80 C69,80 57,73 51,63Z',
	};
	const ex = 35 + (o.ex ?? 0);
	const ey = 37 + (o.ey ?? 0);
	const TUFT = o.ruffle ? `<path d="M33,24 L31,12 L38,21 L42,10 L45,22 Z" fill="${ink}"/>` : '';
	const LEGS = legs
		? `<path d="M48,88 L46.5,106 M42,106h9.5 M59,89 L59,106 M55,106h9.5" fill="none" stroke="${legC}" stroke-width="3.4" stroke-linecap="round"/>`
		: '';
	// The hat sits in the head's group, so it turns with the head. Santa's is self-coloured
	// (red cap, white fur + pompom) so it reads festive in either printing.
	const SANTA_RED = '#c62f2f';
	const SANTA_FUR = '#f7f3ea';
	const HAT =
		o.hat === 'witch'
			? `<g transform="rotate(-8 41 20)"><path d="M18,24 C30,20 54,20 66,24 C56,27 28,27 18,24Z" fill="${ink}"/><path d="M29,23 L44.5,2.5 C46,0.5 48.5,1 49.5,3.5 L53,23Z" fill="${ink}"/><path d="M29,19 L53,19 L54,23 L28,23Z" fill="${tailC}"/></g>`
			: o.hat === 'santa'
				? `<g transform="rotate(-6 41 22)"><path d="M22,24 C26,9 44,2 59,8 C52,13 49,18 46,23Z" fill="${SANTA_RED}"/><path d="M17,24 C29,19.5 53,19.5 63,24 C53,27.5 27,27.5 17,24Z" fill="${SANTA_FUR}"/><circle cx="59" cy="8" r="4.4" fill="${SANTA_FUR}"/></g>`
				: '';
	// The eye sits in its own group so the idle pose can blink (CSS, `.nib-eye`).
	return `${LEGS}<g transform="rotate(${tilt} 54 106)"><path d="${TAIL}" fill="${tailC}"/><path d="${BODY}" fill="${ink}"/><g transform="rotate(${headR} 44 50)">${TUFT}<circle cx="41" cy="36" r="17.5" fill="${ink}"/><path d="${BEAK}" fill="${ink}"/><path d="${LEAF}" fill="${face}"/><g class="nib-eye">${nibEye(eye, ex, ey, ink, face)}</g>${HAT}</g><path d="${WINGS[wing]}" fill="${ink}" stroke="${face}" stroke-width="2.5" stroke-linejoin="round"/></g>`;
}

/** A loose feather (the Ruffled pose). */
const FEATHER = (x: number, y: number, r: number, ink: string, face: string) =>
	`<g transform="translate(${x} ${y}) rotate(${r})"><path d="M0,0 C6,-6 16,-6 22,0 C16,6 6,6 0,0Z" fill="${ink}"/><path d="M-3,0 L16,0" stroke="${face}" stroke-width="1.6" stroke-linecap="round"/></g>`;

/**
 * Nib's poses, from the mock's POSES (each in a 120×120 box):
 * - `idle`: Today, perched on the board's top edge. Calm, upright, one eye on you.
 * - `happy`: the DONE. poster. Eyes shut, chest out, one wing up.
 * - `cheeky`: "Beat me?" on the challenge landing. Leaning in, beak open, one eyelid down.
 * - `ruffled`: empty and error states. A tuft up, a feather loose.
 */
export type NibPose = 'idle' | 'happy' | 'cheeky' | 'ruffled' | 'cheer' | 'lookup' | 'worried' | 'dive';

/**
 * The three colours Nib is drawn in: his body (`ink`), his face patch and the wing's keyline
 * (`stock`), his tail (`ver`). The brand's by default; the web app passes CSS variables
 * (`--nib-ink`, `--nib-face`, `--nib-tail`), so at night he's printed in the night's ink
 * (a light body with a dark face: the inversion the `cheer` pose already uses on ink).
 */
export interface NibPalette {
	ink: string;
	stock: string;
	ver: string;
}

export function nibPose(pose: NibPose, P: NibPalette = C, { hat }: { hat?: 'witch' | 'santa' } = {}): string {
	const base = { ink: P.ink, face: P.stock, tail: P.ver, legsColour: P.ink, hat };
	switch (pose) {
		case 'idle':
			return nib(base);
		case 'happy':
			return nib({ ...base, eye: 'happy', head: -8, wing: 'lift' });
		case 'cheeky':
			return nib({ ...base, eye: 'smug', beak: 'open', head: -10, wing: 'lift', tilt: -5 });
		case 'ruffled':
			// The mock's Ruffled pose: Nib at .72 with two loose feathers beside.
			return `<g transform="translate(18 8) scale(.72)">${nib({ ...base, eye: 'worry', ruffle: true, head: 6, tilt: 3, wing: 'droop' })}</g>${FEATHER(80, 22, -24, P.ink, P.stock)}${FEATHER(92, 46, 34, P.ink, P.stock)}`;
		// The install poses (docs/mockups/install.html):
		case 'cheer':
			// The confirmation card: `happy`, inverted for an ink card.
			return nib({ eye: 'happy', head: -8, wing: 'lift', ink: P.stock, face: P.ink, tail: P.ver, legsColour: P.stock, hat });
		case 'lookup':
			// The Android guide and the way-out card: head up, eye on the browser's menu.
			return nib({ ...base, head: 12, ex: 1.6, ey: -2.4 });
		case 'worried':
			// The offline strip: ruffled, without the loose feathers (it's a small strip).
			return nib({ ...base, eye: 'worry', ruffle: true, head: 6, tilt: 3, wing: 'droop' });
		case 'dive':
			// The iPhone guide: Nib in a stoop, pointing the way.
			return nibFly({ ink: P.ink, face: P.stock, tail: P.ver, dive: 34 });
	}
}

/** Nib in flight (the install mock's `nibFly`): diving, built facing right in a 120 box. */
export function nibFly(o: { ink?: string; face?: string; tail?: string; dive?: number } = {}): string {
	const ink = o.ink ?? C.ink;
	const face = o.face ?? C.stock;
	const tailC = o.tail ?? C.ver;
	return `<g transform="rotate(${o.dive ?? 12} 60 60) translate(120 0) scale(-1 1)"><path d="M62,46 C70,34 84,26 101,20 C93,30 87,38 81,46Z" fill="${ink}"/><path d="M86,52 L111,62.5 L86,69Z" fill="${tailC}"/><path d="M40,40 C62,38 84,46 96,57 C84,68 62,73 40,71Z" fill="${ink}"/><circle cx="37" cy="55" r="16.5" fill="${ink}"/><path d="M22,51 L6,56.5 L22,60.5Z" fill="${ink}"/><path d="M21,50.5 C28,45.5 41,47 49,55 C41,61 30,62.5 21,59.5Z" fill="${face}"/>${nibEye('dot', 30, 54.5, ink, face)}<path d="M48,57 C58,41 80,29 108,23 C98,35 94,45 84,55 C72,61 58,63 48,57Z" fill="${ink}" stroke="${face}" stroke-width="2.6" stroke-linejoin="round"/></g>`;
}

function round(n: number, p = 3): number {
	return Math.round(n * 10 ** p) / 10 ** p;
}
